import {
  ALM_COOKIE_KEY, CONNECT_ID_TOKEN_KEY, CURRENCY_SIGNS, ECOM_COOKIE_KEY,
} from '@config/constants';
import {
  Product, AdobeProduct,
  UserInfo,
  AdobeEventType,
} from '@types';
import { SiteConfig } from './SiteConfig';
import { getCookieValue } from './cookie';
import { customParseJwt } from './parsing';
import { logError } from './log';

export const convertCurrencyToCode = (currencySymbol: string): string => {
  const currencyMap = Object.fromEntries(
    Object.entries(CURRENCY_SIGNS).map(([code, symbol]) => [symbol, code]),
  );
  return (currencyMap[currencySymbol] || 'usd').toLowerCase();
};

export const transformToAdobeProduct = (product: Product): AdobeProduct => {
  const { isSubscriptionSite } = SiteConfig.getConfig();
  return {
    sku: product.url.match(/-p-([^-]+)/)?.[1] || '',
    category: isSubscriptionSite ? 'ecommerce' : (product.categoryName || '').toLowerCase(),
    type: (product.format || '').toLowerCase(),
    price: product.amount?.toString() || '',
    quantity: product.qty.toString(),
    source: SiteConfig.getConfig().siteCode,
    productName: (product.title || '').toLowerCase(),
    productId: product.parentId || '',
  };
};

const isEventAlreadyFired = (eventType: string): boolean => {
  if (!window.adobeDataLayer || window.adobeDataLayer.length === 0) return false;

  return window.adobeDataLayer.some((item) => 'event' in item && item.event === eventType);
};

export const isCartPageAlreadyLoaded = (): boolean => isEventAlreadyFired(AdobeEventType.CART_VIEW);

export const isPageViewAlreadyFired = (): boolean => isEventAlreadyFired(AdobeEventType.PAGE_VIEW);

export const getUserLoginStatus = (): UserInfo => {
  const ecomToken = getCookieValue(ECOM_COOKIE_KEY);
  if (ecomToken) {
    try {
      const ecomInfo = customParseJwt<{
        aud: string;
        realm_access: { roles: string[] };
        sub: string;
        exp: number;
      }>(ecomToken);
      if (ecomInfo && ecomInfo.exp) {
        const loginType = ecomInfo.aud || 'account';
        const adminType = ecomInfo.realm_access?.roles?.includes('admin') ? 'admin' : 'not-admin';

        let almId = '';
        let connectId = '';

        const almToken = getCookieValue(ALM_COOKIE_KEY);
        if (almToken) {
          try {
            const almInfo = customParseJwt<{
              alm_guid: string;
              exp: number;
            }>(almToken);
            if (almInfo && typeof almInfo === 'object'
              && 'alm_guid' in almInfo && typeof almInfo.alm_guid === 'string') {
              almId = almInfo.alm_guid;
            }
          } catch (error) {
            logError('getUserLoginStatus: Failed to parse ALM token:', error);
            almId = '';
          }
        }

        const idToken = getCookieValue(CONNECT_ID_TOKEN_KEY);
        if (idToken) {
          try {
            const idInfo = customParseJwt<{
              sub: string;
              ids: { alm: string }[];
              exp: number;
            }>(idToken);
            if (idInfo) {
              connectId = idInfo.sub || '';
              if (!almId && Array.isArray(idInfo.ids) && idInfo.ids[0]?.alm) {
                almId = idInfo.ids[0].alm;
              }
            }
          } catch (error) {
            logError('getUserLoginStatus: Failed to parse ID token:', error);
            connectId = '';
          }
        }

        return {
          loginStatus: 'logged-in',
          loginType: loginType.toLowerCase(),
          adminType,
          almId,
          connectId,
        };
      }
    } catch (error) {
      logError('getUserLoginStatus: Failed to parse ecom token:', error);
    }
  }

  return {
    loginStatus: 'not-logged-in',
    loginType: '',
    adminType: '',
    almId: '',
    connectId: '',
  };
};

export const detectPlatform = (): 'web' | 'ios' | 'android' => {
  if (typeof window === 'undefined') {
    return 'web';
  }

  const userAgent = window.navigator.userAgent.toLowerCase();
  if (/iphone|ipad|ipod/.test(userAgent)) {
    return 'ios';
  }

  if (/android/.test(userAgent)) {
    return 'android';
  }

  return 'web';
};
